class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setupDelegatedCartIconAccessibility();
    this.setHeaderCartIconAccessibility();
  }

  setupDelegatedCartIconAccessibility() {
    if (window.puCartDrawerDelegatedAccessibility) return;
    window.puCartDrawerDelegatedAccessibility = true;

    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');
      if (!cartLink) return;

      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.open !== 'function') return;

      event.preventDefault();
      cartDrawer.open(cartLink);
    });

    document.addEventListener('keydown', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');
      if (!cartLink || event.code.toUpperCase() !== 'SPACE') return;

      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.open !== 'function') return;

      event.preventDefault();
      cartDrawer.open(cartLink);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    if (cartLink.dataset.cartDrawerBound === 'true') return;
    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.syncHeaderCartIconState();
    this.classList.remove('is-updating');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  syncHeaderCartIconState() {
    const hasItems = !this.classList.contains('is-empty') || Boolean(this.querySelector('.cart-item'));
    if (!hasItems) return;

    const header = document.querySelector('.header');
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    header?.classList.remove('header--empty-cart-quick-buy');
    cartLink.classList.remove('header__icon--quick-buy');
    cartLink.querySelector('.header__quick-buy-text')?.remove();
  }

  setSummaryAccessibility(cartDrawerNote) {
    if (!cartDrawerNote) return;
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];

      if (section.id === 'cart-icon-bubble' && sectionElement && sectionHtml) {
        const nextCartIcon = this.getSectionDOM(sectionHtml, '#cart-icon-bubble');

        if (nextCartIcon) {
          sectionElement.replaceWith(nextCartIcon);
          this.setHeaderCartIconAccessibility();
          return;
        }
      }

      const nextHtml = sectionHtml ? this.getSectionInnerHTML(sectionHtml, section.selector) : null;
      if (sectionElement && nextHtml) sectionElement.innerHTML = nextHtml;
    });
    this.syncHeaderCartIconState();
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.querySelector('#CartDrawer-Checkout')?.removeAttribute('disabled');
    this.classList.remove('is-updating');

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (this.classList.contains('active')) {
        document.body.classList.add('overflow-hidden');
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
